import { catchError, map, merge, retry, Subject, takeUntil, tap } from "rxjs";
import { forActions } from "@snap/state-management";
import type { Lens } from "../lens/Lens";
import type { LensState } from "../session/lensState";
import type { SessionState } from "../session/sessionState";
import type { Uri, UriCancelRequest, UriHandler, UriRequest, UriResponse } from "./UriHandlers";

type LensId = string;
type RequestId = string;

export type UriRequestEvent = {
    request: UriRequest;
    reply: (response: UriResponse) => void;
    lens: Lens;
};

type UriCancelRequestEvent = {
    request: UriCancelRequest;
    lens: Lens;
};

interface LensRequestState<T = undefined> {
    cancellationHandlers: Map<RequestId, RemoteApiCancelRequestHandler>;
    custom: T;
}

/**
 * Represents a Remote API request cancellation handler function.
 */
export type RemoteApiCancelRequestHandler = () => void;

/**
 * Invokes the cancellation handler associated with the provided key and removes it from the collection of handlers.
 */
function callCancellationHandler(
    cancellationHandlers: Map<RequestId, RemoteApiCancelRequestHandler>,
    ...keys: RequestId[]
) {
    for (const key of keys) {
        cancellationHandlers.get(key)?.();
        cancellationHandlers.delete(key);
    }
}

/**
 * Removes the specified lenses' metadata from the cache and invokes their cancellation callbacks.
 *
 * @param lensRequestState The state representing the lens cache.
 * @param lensIds An array of lens IDs to be removed from the cache
 * and for which the cancellation callbacks will be invoked.
 */
function handleLensApplicationEnd(lensRequestState: Map<LensId, LensRequestState<any>>, ...lensIds: LensId[]) {
    for (const lensId of lensIds) {
        const state = lensRequestState.get(lensId);
        if (state) {
            callCancellationHandler(state.cancellationHandlers, ...state.cancellationHandlers.keys());
            lensRequestState.delete(lensId);
        }
    }
}

export type CreateLensRequestState<State> = (request: UriRequest, lens: Lens) => State;

export type ProcessRequest<State> = (input: {
    request: UriRequest;
    reply: (response: UriResponse) => void;
    lens: Lens;
    setCancellationHandler: (fn: () => void) => void;
    state: State;
}) => void;

export type ProcessError = (error: Error) => void;

/**
 * Creates a UriHandler for the provided URI.
 * Handles lens and sessions state events, so provides clear abstraction for internal usages.
 *
 * @internal
 */
export function createUriRequestProcessor<State = undefined>({
    uri,
    lensState,
    sessionState,
    createLensRequestState,
    processRequest,
    processInternalError,
}: {
    uri: Uri | Uri[];
    lensState: LensState;
    sessionState: SessionState;
    createLensRequestState?: CreateLensRequestState<State>;
    processRequest: ProcessRequest<State>;
    processInternalError: ProcessError;
}): UriHandler {
    const uriRequests = new Subject<UriRequestEvent>();
    const uriCancelRequests = new Subject<UriCancelRequestEvent>();
    const lensRequestState = new Map<LensId, LensRequestState<State>>();

    const lensTurnOffEvents = lensState.events.pipe(
        forActions("turnedOff"),
        tap(([action]) => handleLensApplicationEnd(lensRequestState, action.data.id))
    );

    const uriRequestEvents = uriRequests.pipe(
        map(({ request, lens, reply }) => {
            if (!lensRequestState.has(lens.id)) {
                const custom = createLensRequestState?.(request, lens);
                const state: LensRequestState<State> = {
                    // Prepares a collection to store cancellation handlers.
                    // A specific handler will be invoked when a cancellation request is issued by the lens.
                    // All handlers will be invoked when the lens is replaced with another one or the session
                    // is destroyed.
                    cancellationHandlers: new Map(),
                    // Safety: it is fine for State to be undefined
                    custom: custom as State,
                };
                lensRequestState.set(lens.id, state);
            }
            const requestState = lensRequestState.get(lens.id)!;

            return {
                request,
                reply,
                lens,
                state: requestState.custom,
                setCancellationHandler: (fn: () => any) => {
                    requestState.cancellationHandlers.set(request.identifier, fn);
                },
            };
        }),

        tap(processRequest)
    );

    const uriCancelRequestEvents = uriCancelRequests.pipe(
        tap((uriRequest) => {
            const cancellationHandlers = lensRequestState.get(uriRequest.lens.id)?.cancellationHandlers;
            if (cancellationHandlers) {
                callCancellationHandler(cancellationHandlers, uriRequest.request.requestId);
            }
        })
    );

    merge(lensTurnOffEvents, uriRequestEvents, uriCancelRequestEvents)
        .pipe(
            catchError((error, sourcePipe) => {
                processInternalError(error);
                // Return the source pipe so that we can retry the pipe instead of just completing it.
                return sourcePipe;
            }),
            // When the pipe completes due to an error,
            // we want to resubscribe to the original pipe to keep it alive.
            retry(),
            takeUntil(sessionState.events.pipe(forActions("destroy")))
        )
        .subscribe({
            complete: () => handleLensApplicationEnd(lensRequestState, ...lensRequestState.keys()),
        });

    return {
        uri,

        handleRequest(request, reply, lens) {
            uriRequests.next({ request, reply, lens });
        },

        cancelRequest(request, lens) {
            uriCancelRequests.next({ request, lens });
        },
    };
}
